import { validateRedirectTo } from '@utils/redirect';
import { ToolkitsEnum } from '@authFlow/pages/ToolkitSelection/types';

import type { SignupStepRequiredT } from '@authFlow/types/types';

export const TRIAL_SOURCES = ['stories'];

export const TRIAL_SOURCES_LP = 'lp-';

const OFFER_REGEXP = new RegExp('^[a-zA-Z0-9-_]+$');

const cmpRedirectTo = [
  '/topic-research/',
  '/seo-content-template/',
  '/swa/',
  '/post-tracking/',
  '/content-audit/',
];

export const Custom = {
  competitiveIntelligence: 'ci',
  contentMarketingPlatform: 'cmp',
  secure: 'secure',
  promo: 'promo',
  smm: 'smm',
  contentAI: 'content-ai',
  siteAudit: 'sa',
  keywords: 'keywords',
  trends: 'trends',
  competitiveResearch: 'cr',
  extendedTrial: 'extended-trial',
  backlinko: 'backlinko',
  copilot: 'copilot',
  contentToolkit: 'content-toolkit',
  localToolkit: 'local-toolkit',
  advertisingToolkit: 'advertising-toolkit',
  aiToolkit: 'ai-toolkit',
  socialToolkit: 'social-toolkit',
  trafficToolkit: 'traffic-and-market-toolkit',
  seoToolkit: 'seo-toolkit',
  prToolkit: 'pr-toolkit',
  aiSeo: 'ai-seo',
  semrushOne: 'semrush-one',
};

type Config = {
  trial?: string; // 'new' | 'force' | 'off',
  redirectTo?: string;
  custom?: string;
  src?: string;
  tk?: string; // not in use anymore
  offer?: string;
  promo?: string;
  promocode?: string; // for billing form
  currency?: string; // for billing form
  survey?: string; // 'new' | 'force' | 'off'
  getSubscription?: SignupStepRequiredT; // 'new' | 'force' | 'off'
  invite?: string; // 'new' | 'force' | 'off'
  contactInfo?: string; // 'new' | 'force' | 'off'
  offerInfo?: {
    trial_offer?: boolean;
    trial_period?: number;
    offer_hash?: string;
  };
  toolkit?: string;
  toolkitSelection?: string; // 'new' | 'force' | 'off'
  marketingSource?: string; // 'new' | 'force' | 'off'
};

export const parseConfigFromUrl = (): Config => {
  const urlParams = new URLSearchParams(window.location.search);

  const rawConfig: Config = {
    trial: getAllowedValue(urlParams.get('trial')),
    redirectTo: validateRedirectTo(urlParams.get('redirect_to')),
    custom: urlParams.get('custom'),
    src: urlParams.get('src'),
    tk: getAllowedDashboard(urlParams.get('tk')),
    offer: validateOffer(urlParams.get('offer')),
    promo: urlParams.get('promo'),
    promocode: urlParams.get('promocode'),
    currency: urlParams.get('currency'),
    survey: getAllowedValue(urlParams.get('survey')),
    toolkit: getAllowedToolkit(urlParams.get('toolkit')),
  };

  updateTrial(rawConfig);
  updateSurvey(rawConfig);
  updateCustom(rawConfig);
  updateDashboard(rawConfig);
  updateGetSubscriptions(rawConfig);
  updateOfferInfo(rawConfig);
  updatePromocode(rawConfig);
  updateToolkitSelection(rawConfig);
  updateMarketingSource(rawConfig);

  return removeEmptyStrings(rawConfig);
};

const defaultConfig: Config = {
  trial: 'new',
  survey: 'new',
  invite: 'off',
  contactInfo: 'off',
  toolkitSelection: 'off',
  marketingSource: 'new',
};

export const initConfigFromUrl = (): Config => {
  const config = parseConfigFromUrl();

  for (const key in defaultConfig) {
    if (!(key in config)) {
      config[key] = defaultConfig[key];
    }
  }

  return config;
};

function updateTrial(config: Config) {
  if (config.offer) {
    config.trial = 'off';
    return config;
  }

  if (config.trial === 'new' && isForceTrialSource(config.src)) {
    config.trial = 'force';
    return config;
  }

  return config;
}

function updateSurvey(config: Config) {
  if (config.offer || config.promo || config.custom === 'promo') {
    config.survey = 'off';
    return config;
  }

  return config;
}

function updateCustom(config: Config) {
  if (
    typeof config.src === 'string' &&
    (config.src.startsWith('cmp') ||
      (config.src === 'header' &&
        typeof config.redirectTo === 'string' &&
        cmpRedirectTo.includes(config.redirectTo)))
  ) {
    config.custom = 'cmp';
    return config;
  }

  if (
    typeof config.src === 'string' &&
    config.src.startsWith('lp-traffic-analytics') &&
    typeof config.toolkit !== 'string'
  ) {
    config.custom = 'ci';
    return config;
  }

  if (
    typeof config.toolkit === 'string' &&
    config.toolkit !== 'all' &&
    !config.custom
  ) {
    config.custom = `${config.toolkit}-toolkit`;
    return config;
  }

  return config;
}

function updateDashboard(config: Config) {
  if (config.custom === 'cmp') {
    config.tk = 'content-marketing';
    return config;
  }

  return config;
}

function updateGetSubscriptions(config: Config) {
  const redirects = {
    [Custom.smm]: '/social-media/',
    [Custom.contentAI]: '/social-content-ai/',
  };

  switch (config.custom) {
    case Custom.smm:
    case Custom.contentAI:
      config.trial = 'off';
      config.getSubscription = 'new';
      config.survey = 'new';
      break;
    default:
      break;
  }

  if (
    [Custom.smm, Custom.contentAI].includes(config.custom) &&
    !config.redirectTo
  ) {
    config.redirectTo = redirects[config.custom];
  }

  return config;
}

function updateOfferInfo(config: Config) {
  const { trial_offer, trial_period, offer_hash } =
    window.__AUTH_STATE__?.offerInfo || {};

  if (
    trial_offer !== undefined &&
    trial_period !== undefined &&
    offer_hash !== undefined
  ) {
    config.offerInfo = { trial_offer, trial_period, offer_hash };
  }

  return config;
}

function updatePromocode(config: Config) {
  if (config.offer && config.promo) {
    config.promocode = config.promo;
    config.promo = null;

    return config;
  }

  return config;
}

function removeEmptyStrings(obj: any): any {
  const newObj = {};
  Object.keys(obj).forEach((prop) => {
    if (obj[prop]) {
      newObj[prop] = obj[prop];
    }
  });

  return newObj;
}

function getAllowedValue(value) {
  const allowedValues = ['new', 'force', 'off'];
  if (allowedValues.includes(value)) {
    return value;
  }
  return null;
}

function getAllowedToolkit(value) {
  const validToolkits = [...Object.keys(ToolkitsEnum), 'all'];
  if (validToolkits.includes(value)) {
    return value;
  }
  return null;
}

function getAllowedDashboard(value) {
  const allowedValues = [
    'seo',
    'ppc',
    'social-media',
    'content-marketing',
    'competitive-research',
  ];

  if (allowedValues.includes(value)) {
    return value;
  }
  return null;
}

function isForceTrialSource(src) {
  return (
    TRIAL_SOURCES.includes(src) ||
    (typeof src === 'string' && src.startsWith(TRIAL_SOURCES_LP))
  );
}

function updateToolkitSelection(config: Config) {
  if (config.toolkit === 'all') {
    config.toolkitSelection = 'force';
    return config;
  }

  return config;
}

function updateMarketingSource(config: Config) {
  if (config.promo || config.custom === 'promo') {
    config.marketingSource = 'off';
    return config;
  }

  return config;
}

export function validateOffer(offer: string | null | undefined) {
  if (offer && OFFER_REGEXP.test(offer)) {
    return offer;
  }
  return null;
}
